<template>
  <v-container class="py-6">
    <v-row justify="center">
      <v-col cols="12" md="10" lg="8">
        <v-card elevation="4">
          <v-card-title class="text-h5">
            Ollama
          </v-card-title>
          <v-card-subtitle>
            Anfrage an <code>{{ currentEndpoint }}</code>
          </v-card-subtitle>

          <v-card-text>
            <v-text-field
              v-model="model"
              label="Model"
              variant="outlined"
              density="comfortable"
              prepend-inner-icon="mdi-cube-outline"
            />

            <v-card
              variant="outlined"
              class="mt-4"
            >
              <v-card-title class="text-subtitle-1">
                Available Models
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="modelsBasePath"
                      label="Models Route"
                      variant="outlined"
                      density="comfortable"
                      hint="Wird an die API-Basisadresse angehaengt, z. B. /models"
                      persistent-hint
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="modelsProvider"
                      label="Provider"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="3" class="d-flex align-center">
                    <v-btn
                      color="primary"
                      block
                      :loading="modelsLoading"
                      :disabled="modelsLoading"
                      @click="fetchAvailableModels"
                    >
                      Load Models
                    </v-btn>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="modelsError"
                  type="error"
                  variant="tonal"
                  class="mt-4"
                >
                  {{ modelsError }}
                </v-alert>

                <v-list
                  v-if="availableModels.length"
                  class="mt-4"
                  density="compact"
                >
                  <v-list-item
                    v-for="availableModel in availableModels"
                    :key="availableModel.digest || availableModel.model"
                    :title="availableModel.name"
                    :subtitle="buildModelSubtitle(availableModel)"
                    @click="selectModel(availableModel)"
                  />
                </v-list>

                <div
                  v-else-if="modelsLoaded && !modelsLoading && !modelsError"
                  class="text-medium-emphasis mt-4"
                >
                  Keine Modelle gefunden.
                </div>
              </v-card-text>
            </v-card>

            <v-card
              variant="outlined"
              class="mt-4"
            >
              <v-card-title class="text-subtitle-1">
                Pull Model
              </v-card-title>
              <v-card-subtitle>
                POST <code>{{ endpointPull }}</code>
              </v-card-subtitle>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="pullModelName"
                      label="Pull Model"
                      variant="outlined"
                      density="comfortable"
                      prepend-inner-icon="mdi-download"
                    />
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-switch
                      v-model="pullInsecure"
                      label="Insecure"
                      color="primary"
                      inset
                    />
                  </v-col>
                  <v-col cols="12" md="6" class="d-flex align-center">
                    <v-btn
                      color="primary"
                      block
                      :loading="pullLoading"
                      :disabled="pullLoading"
                      @click="pullModel"
                    >
                      Pull Model
                    </v-btn>
                  </v-col>
                </v-row>

                <v-alert
                  v-if="pullError"
                  type="error"
                  variant="tonal"
                  class="mt-4 pull-error-alert"
                >
                  {{ pullError }}
                </v-alert>

                <v-card
                  v-if="pullResult"
                  variant="tonal"
                  class="mt-4"
                >
                  <v-card-text>
                    <pre class="json-box">{{ formatJson(pullResult) }}</pre>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>

            <v-textarea
              v-model="system"
              label="System Prompt"
              variant="outlined"
              rows="3"
              auto-grow
              class="mt-4"
            />

            <v-textarea
              v-model="prompt"
              label="Prompt"
              variant="outlined"
              rows="8"
              auto-grow
              class="mt-4"
            />


            <v-switch
              v-model="stream"
              label="Stream"
              color="primary"
              inset
              class="mt-2"
            />

            <div class="d-flex flex-wrap ga-3 mt-4">
              <v-btn
                color="primary"
                :loading="loading"
                :disabled="loading"
                @click="generateText"
              >
                Generieren
              </v-btn>

              <v-btn
                v-if="loading && stream"
                color="error"
                variant="outlined"
                @click="stopStreaming"
              >
                Stop
              </v-btn>

              <v-btn
                variant="outlined"
                :disabled="loading"
                @click="resetForm"
              >
                Zurücksetzen
              </v-btn>
            </div>

            <v-alert
              v-if="errorMessage"
              type="error"
              variant="tonal"
              class="mt-4"
            >
              {{ errorMessage }}
            </v-alert>

            <v-card
              v-if="responseText || loading"
              variant="outlined"
              class="mt-6"
            >
              <v-card-title class="text-subtitle-1">
                Antwort
              </v-card-title>
              <v-card-subtitle>
                Modell: {{ responseModel || model }}
              </v-card-subtitle>
              <v-card-text>
                <div
                  ref="responseBox"
                  class="response-box"
                >
                  <span>{{ responseText }}</span>
                  <span
                    v-if="showStreamingCursor"
                    class="streaming-cursor"
                    aria-hidden="true"
                  >█</span>
                </div>
              </v-card-text>
            </v-card>

            <v-divider class="my-6" />

            <v-card
              variant="outlined"
              class="mt-6"
            >
              <v-card-title class="text-subtitle-1">
                Article Sentiment
              </v-card-title>
              <v-card-text>
                <v-text-field
                  v-model="sentimentBasePath"
                  label="Sentiment Base Path"
                  variant="outlined"
                  density="comfortable"
                  hint="Wird an die API-Basisadresse angehaengt, z. B. /ai/sentiment"
                  persistent-hint
                  class="mb-4"
                />

                <v-row>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="articleSentiment.articleId"
                      label="Article ID"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="articleSentiment.provider"
                      label="Provider"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field
                      v-model="articleSentiment.model"
                      label="Model Override"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap ga-3">
                  <v-btn
                    color="primary"
                    :loading="articleSentiment.loading"
                    :disabled="articleSentiment.loading"
                    @click="analyzeArticleSentiment"
                  >
                    Analyze Article
                  </v-btn>
                </div>

                <v-alert
                  v-if="articleSentiment.error"
                  type="error"
                  variant="tonal"
                  class="mt-4"
                >
                  {{ articleSentiment.error }}
                </v-alert>

                <v-card
                  v-if="articleSentiment.result"
                  variant="tonal"
                  class="mt-4"
                >
                  <v-card-text>
                    <pre class="json-box">{{ formatJson(articleSentiment.result) }}</pre>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>

            <v-card
              variant="outlined"
              class="mt-6"
            >
              <v-card-title class="text-subtitle-1">
                Stock Sentiment
              </v-card-title>
              <v-card-text>
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="stockSentiment.stockId"
                      label="Stock ID"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="stockSentiment.articleLimit"
                      label="Article Limit"
                      type="number"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="stockSentiment.provider"
                      label="Provider"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="stockSentiment.model"
                      label="Model Override"
                      variant="outlined"
                      density="comfortable"
                    />
                  </v-col>
                </v-row>

                <div class="d-flex flex-wrap ga-3">
                  <v-btn
                    color="primary"
                    :loading="stockSentiment.loading"
                    :disabled="stockSentiment.loading"
                    @click="aggregateStockSentiment"
                  >
                    Analyze Stock
                  </v-btn>
                </div>

                <v-alert
                  v-if="stockSentiment.error"
                  type="error"
                  variant="tonal"
                  class="mt-4"
                >
                  {{ stockSentiment.error }}
                </v-alert>

                <v-card
                  v-if="stockSentiment.result"
                  variant="tonal"
                  class="mt-4"
                >
                  <v-card-text>
                    <pre class="json-box">{{ formatJson(stockSentiment.result) }}</pre>
                  </v-card-text>
                </v-card>
              </v-card-text>
            </v-card>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios, { getApiBaseUrl } from '@/api/client'
import { getEnvironmentLabel, usesHostedBackend } from '@/config/appConfig'

export default {
  name: 'OllamaScreen',

  data () {
    return {
      endpointJson: '/generate',
      endpointStream: '/generate_stream',
      endpointPull: '/pull',
      prompt: '',
      model: 'llama3.2:1b',
      system: '',
      bearerToken: '',
      stream: true,
      loading: false,
      responseText: '',
      responseModel: '',
      errorMessage: '',
      modelsBasePath: '/models',
      modelsProvider: 'ollama',
      modelsLoading: false,
      modelsLoaded: false,
      modelsError: '',
      availableModels: [],
      pullModelName: 'gemma3:1b',
      pullInsecure: false,
      pullLoading: false,
      pullError: '',
      pullResult: null,
      sentimentBasePath: '/ai/sentiment',
      abortController: null,
      autoScrollScheduled: false,
      wasStoppedByUser: false,
      showStreamingCursor: false,
      articleSentiment: {
        articleId: '',
        provider: 'ollama',
        model: '',
        loading: false,
        result: null,
        error: ''
      },
      stockSentiment: {
        stockId: '',
        articleLimit: 8,
        provider: 'ollama',
        model: '',
        loading: false,
        result: null,
        error: ''
      }
    }
  },

  computed: {
    currentEndpoint () {
      return this.stream ? this.endpointStream : this.endpointJson
    }
  },

  mounted () {
    this.configureApiBase()
  },

  methods: {
    configureApiBase () {
      const baseUrl = getApiBaseUrl()

      console.log(`--${getEnvironmentLabel()}--`)

      if (usesHostedBackend()) {
        this.endpointJson = '/generate'
        this.endpointStream = '/generate_stream'
        this.endpointPull = '/pull'
      } else {
        this.endpointJson = '/generate'
        this.endpointStream = `${baseUrl}/generate_stream`
        this.endpointPull = '/pull'
      }

      console.log('endpointJson:', this.endpointJson)
      console.log('endpointStream:', this.endpointStream)
      console.log('endpointPull:', this.endpointPull)
      console.log('api baseURL:', baseUrl)
    },

    resetForm () {
      if (this.loading) {
        this.stopStreaming()
      }

      this.prompt = ''
      this.system = ''
      this.stream = false
      this.responseText = ''
      this.responseModel = ''
      this.errorMessage = ''
      this.showStreamingCursor = false
    },

    buildHeaders (isStreaming = false) {
      const headers = {}

      if (isStreaming) {
        headers.Accept = 'text/plain'
      } else {
        headers.Accept = 'application/json'
      }

      headers['Content-Type'] = 'application/json'

      if (this.bearerToken.trim()) {
        headers.Authorization = `Bearer ${this.bearerToken.trim()}`
      }

      return headers
    },

    buildSentimentParams (provider, model, extra = {}) {
      const params = {
        provider: (provider || 'ollama').trim()
      }

      if ((model || '').trim()) {
        params.model = model.trim()
      }

      return {
        ...params,
        ...extra
      }
    },

    buildSentimentUrl (resource, id) {
      const basePath = `/${String(this.sentimentBasePath || '')
        .trim()
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')}`

      return `${basePath}/${resource}/${id}`
    },

    buildModelsUrl () {
      return `/${String(this.modelsBasePath || '')
        .trim()
        .replace(/^\/+/, '')
        .replace(/\/+$/, '')}`
    },

    normalizeModelList (payload) {
      const candidates = Array.isArray(payload)
        ? payload
        : payload?.models || payload?.data || payload?.items || []

      return candidates
        .map((item) => {
          if (typeof item === 'string') {
            return {
              name: item,
              model: item,
              size: null,
              modified_at: '',
              digest: item,
              details: {}
            }
          }

          const fallbackName = item?.name || item?.model || item?.id || ''
          if (!fallbackName) {
            return null
          }

          return {
            name: fallbackName,
            model: item?.model || fallbackName,
            size: item?.size ?? null,
            modified_at: item?.modified_at || '',
            digest: item?.digest || item?.model || fallbackName,
            details: item?.details || {}
          }
        })
        .filter(Boolean)
    },

    selectModel (availableModel) {
      this.model = availableModel?.model || availableModel?.name || ''
    },

    buildModelSubtitle (availableModel) {
      const parts = []

      if (availableModel?.model && availableModel.model !== availableModel.name) {
        parts.push(availableModel.model)
      }

      if (availableModel?.details?.family) {
        parts.push(availableModel.details.family)
      }

      if (availableModel?.details?.parameter_size) {
        parts.push(availableModel.details.parameter_size)
      }

      if (availableModel?.details?.quantization_level) {
        parts.push(availableModel.details.quantization_level)
      }

      if (availableModel?.size) {
        parts.push(this.formatBytes(availableModel.size))
      }

      if (availableModel?.modified_at) {
        parts.push(this.formatDateTime(availableModel.modified_at))
      }

      return parts.join(' | ')
    },

    formatBytes (value) {
      const size = Number(value)
      if (!Number.isFinite(size) || size <= 0) {
        return ''
      }

      const units = ['B', 'KB', 'MB', 'GB', 'TB']
      let unitIndex = 0
      let formattedSize = size

      while (formattedSize >= 1024 && unitIndex < units.length - 1) {
        formattedSize /= 1024
        unitIndex += 1
      }

      const decimals = formattedSize >= 10 || unitIndex === 0 ? 0 : 1
      return `${formattedSize.toFixed(decimals)} ${units[unitIndex]}`
    },

    formatDateTime (value) {
      if (!value) {
        return ''
      }

      const date = new Date(value)
      if (Number.isNaN(date.getTime())) {
        return value
      }

      return date.toLocaleString('de-DE')
    },

    formatJson (value) {
      try {
        return JSON.stringify(value, null, 2)
      } catch (error) {
        return String(value ?? '')
      }
    },

    extractErrorMessage (error, fallbackMessage) {
      const responseData = error?.response?.data
      const candidates = [
        responseData?.detail,
        responseData?.error,
        responseData?.message,
        error?.message
      ]

      for (const candidate of candidates) {
        if (!candidate) {
          continue
        }

        if (typeof candidate === 'string') {
          return candidate
        }

        try {
          return JSON.stringify(candidate, null, 2)
        } catch (jsonError) {
          return String(candidate)
        }
      }

      return fallbackMessage
    },

    async fetchAvailableModels () {
      this.modelsLoading = true
      this.modelsLoaded = false
      this.modelsError = ''
      this.availableModels = []

      try {
        const { data } = await axios.get(
          this.buildModelsUrl(),
          {
            headers: this.buildHeaders(false),
            params: {
              provider: (this.modelsProvider || 'ollama').trim() || 'ollama'
            }
          }
        )

        this.availableModels = this.normalizeModelList(data)
        this.modelsLoaded = true
      } catch (error) {
        this.modelsError = this.extractErrorMessage(error, 'Models request failed.')
      } finally {
        this.modelsLoading = false
      }
    },

    async pullModel () {
      const model = String(this.pullModelName || '').trim()
      if (!model) {
        this.pullError = 'Bitte ein gueltiges Model eingeben.'
        this.pullResult = null
        return
      }

      this.pullLoading = true
      this.pullError = ''
      this.pullResult = null

      try {
        const response = await axios.post(
          this.endpointPull,
          {
            model,
            insecure: Boolean(this.pullInsecure),
            stream: false
          },
          {
            headers: this.buildHeaders(false)
          }
        )

        this.pullResult = response?.data ?? null

        if (!response) {
          throw new Error('Pull request failed without response object.')
        }
      } catch (error) {
        this.pullError = this.extractErrorMessage(error, 'Pull request failed.')
      } finally {
        this.pullLoading = false
      }
    },

    async analyzeArticleSentiment () {
      const articleId = Number(this.articleSentiment.articleId)
      if (!Number.isInteger(articleId) || articleId <= 0) {
        this.articleSentiment.error = 'Bitte eine gueltige Article ID eingeben.'
        this.articleSentiment.result = null
        return
      }

      this.articleSentiment.loading = true
      this.articleSentiment.error = ''
      this.articleSentiment.result = null

      try {
        const { data } = await axios.post(
          this.buildSentimentUrl('article', articleId),
          null,
          {
            headers: this.buildHeaders(false),
            params: this.buildSentimentParams(
              this.articleSentiment.provider,
              this.articleSentiment.model
            )
          }
        )

        this.articleSentiment.result = data
      } catch (error) {
        this.articleSentiment.error = this.extractErrorMessage(error, 'Article sentiment request failed.')
      } finally {
        this.articleSentiment.loading = false
      }
    },

    async aggregateStockSentiment () {
      const stockId = Number(this.stockSentiment.stockId)
      const articleLimit = Number(this.stockSentiment.articleLimit)

      if (!Number.isInteger(stockId) || stockId <= 0) {
        this.stockSentiment.error = 'Bitte eine gueltige Stock ID eingeben.'
        this.stockSentiment.result = null
        return
      }

      if (!Number.isInteger(articleLimit) || articleLimit <= 0) {
        this.stockSentiment.error = 'Bitte ein gueltiges Article Limit eingeben.'
        this.stockSentiment.result = null
        return
      }

      this.stockSentiment.loading = true
      this.stockSentiment.error = ''
      this.stockSentiment.result = null

      try {
        const { data } = await axios.post(
          this.buildSentimentUrl('stock', stockId),
          null,
          {
            headers: this.buildHeaders(false),
            params: this.buildSentimentParams(
              this.stockSentiment.provider,
              this.stockSentiment.model,
              { article_limit: articleLimit }
            )
          }
        )

        this.stockSentiment.result = data
      } catch (error) {
        this.stockSentiment.error = this.extractErrorMessage(error, 'Stock sentiment request failed.')
      } finally {
        this.stockSentiment.loading = false
      }
    },

    scheduleAutoScroll () {
      if (this.autoScrollScheduled) {
        return
      }

      this.autoScrollScheduled = true

      requestAnimationFrame(() => {
        this.autoScrollScheduled = false
        const box = this.$refs.responseBox
        if (box) {
          box.scrollTop = box.scrollHeight
        }
      })
    },

    stopStreaming () {
      this.wasStoppedByUser = true
      this.showStreamingCursor = false

      if (this.abortController) {
        this.abortController.abort()
      }
    },

    async generateText () {
      if (!this.prompt.trim()) {
        this.errorMessage = 'Bitte einen Prompt eingeben.'
        this.responseText = ''
        return
      }

      this.loading = true
      this.wasStoppedByUser = false
      this.errorMessage = ''
      this.responseText = ''
      this.responseModel = this.model || 'llama3.2:1b'
      this.showStreamingCursor = false

      try {
        if (this.stream) {
          await this.generateTextStream()
        } else {
          await this.generateTextJson()
        }
      } catch (error) {
        if (error?.name === 'AbortError') {
          if (!this.wasStoppedByUser) {
            this.errorMessage = 'Streaming wurde abgebrochen.'
          }
          return
        }

        this.errorMessage = this.extractErrorMessage(error, 'Unbekannter Fehler')
      } finally {
        this.loading = false
        this.abortController = null
        this.showStreamingCursor = false
      }
    },

    async generateTextJson () {
      const { data } = await axios.post(
        this.endpointJson,
        {
          prompt: this.prompt,
          model: this.model || 'llama3.2:1b',
          system: this.system,
          stream: false
        },
        {
          headers: this.buildHeaders(false)
        }
      )

      this.responseText = data?.response || ''
      this.responseModel = data?.model || this.model
      this.scheduleAutoScroll()

      if (!this.responseText) {
        this.errorMessage = 'Der Server hat keine Antwort im Feld "response" geliefert.'
      }
    },

    async generateTextStream () {
      this.abortController = new AbortController()
      this.showStreamingCursor = true

      const response = await fetch(this.endpointStream, {
        method: 'POST',
        headers: this.buildHeaders(true),
        body: JSON.stringify({
          prompt: this.prompt,
          model: this.model || 'llama3.2:1b',
          system: this.system,
          stream: true
        }),
        credentials: 'include',
        signal: this.abortController.signal
      })

      console.log('Final URL:', response.url)
      console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)
      console.log('Content-Type:', response.headers.get('content-type'))

      if (!response.ok) {
        this.showStreamingCursor = false

        let errorText = ''
        try {
          errorText = await response.text()
        } catch (e) {
          errorText = ''
        }

        throw new Error(errorText || `HTTP-Fehler ${response.status}`)
      }

      if (!response.body) {
        this.showStreamingCursor = false
        throw new Error('Streaming wird vom Browser nicht unterstützt oder der Response-Body ist leer.')
      }

      const reader = response.body.getReader()
      const decoder = new TextDecoder('utf-8')

      this.responseText = ''

      let done = false

      while (!done) {
        const result = await reader.read()
        done = result.done

        if (result.value) {
          const chunkText = decoder.decode(result.value, { stream: true })
          console.log('Chunk:', chunkText.slice(0, 200))
          this.responseText += chunkText
          this.scheduleAutoScroll()
        }
      }

      this.responseText += decoder.decode()
      this.scheduleAutoScroll()
      this.showStreamingCursor = false

      if (!this.responseText && !this.wasStoppedByUser) {
        this.errorMessage = 'Der Stream hat keine Inhalte geliefert.'
      }
    }
  }
}
</script>

<style scoped>
.response-box {
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.6;
  max-height: 420px;
  overflow-y: auto;
  padding: 12px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.json-box {
  white-space: pre-wrap;
  word-break: break-word;
  margin: 0;
  font-family: Consolas, Monaco, monospace;
  line-height: 1.5;
}

.pull-error-alert {
  white-space: pre-wrap;
}

.streaming-cursor {
  display: inline-block;
  margin-left: 2px;
  animation: blink-cursor 1s steps(1) infinite;
}

@keyframes blink-cursor {
  0%,
  50% {
    opacity: 1;
  }
  50.01%,
  100% {
    opacity: 0;
  }
}
</style>
